<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import LightgalleryBase from '~/components/global/LightgalleryBase.vue'
import type { ReferenceDetailResponse } from '~/types/api/ReferenceDetail'

const props = defineProps<{
  referenceDetail: ReferenceDetailResponse
}>()

const lightgalleryState = useLightgalleryState({
  galleryId: 'references-detail-header',
  dynamicItems: computed(() => {
    if (props.referenceDetail.module_data.video_url) {
      return [
        {
          src: props.referenceDetail.module_data.video_url,
          thumb: youtubeThumbnail(props.referenceDetail.module_data.video_url),
        },
      ]
    }

    return [
      {
        src: thumbnail(props.referenceDetail.image, 'lightbox'),
        thumb: thumbnail(props.referenceDetail.image, 'lightbox-thumb'),
      },
    ]
  }),
})

const imageData = getImageSrcSizesData(props.referenceDetail.image, {
  preload: true,
  defaultFormat: '620x1240',
  sizes: `
    (max-width: 991.99px) 95vw,
    (max-width: 1199.99px) 460px,
    (max-width: 1399.99px) 500px,
    620px
  `,
  srcsetToGenerate: [
    ['620x1240', '620w'],
    ['1240x2480', '1240w'],
  ],
})
</script>

<template>
  <div class="c-references-detail-header-image">
    <LightgalleryBase
      :lightgallery-state="lightgalleryState"
      class="c-references-detail-header-image__gallery"
    >
      <button
        class="c-references-detail-header-image__button"
        @click="lightgalleryState.openGalleryAtIndex(0)"
      >
        <Icon
          v-if="referenceDetail.module_data.video_url"
          name="play"
          class="sc-icon"
        />

        <img
          class="sc-img"
          :src="imageData.src"
          :srcset="imageData.srcset"
          :sizes="imageData.sizes"
          :alt="referenceDetail.name || ''"
        />
      </button>
    </LightgalleryBase>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-references-detail-header-image {
  &__button {
    position: relative;
    border: none;
    margin: 0;
    padding: 0;
    cursor: pointer;
    transition: all var(--transition-duration) var(--transition-timing-function);

    .sc-icon {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;
      z-index: 2;
      display: grid;
      place-items: center;
      width: 86px;
      height: 86px;
      border-radius: 86px;
      background-color: var(--color-dark);
      color: var(--color-light);
      font-size: 29px;
      padding-left: 6px;
      transition: all var(--transition-duration)
        var(--transition-timing-function);

      @include breakpoint-down('sm') {
        font-size: 18px;
        width: 54px;
        height: 54px;
      }
    }

    .sc-img {
      width: 100%;
      height: auto;
    }

    &:hover {
      filter: brightness(0.9) grayscale(0.6);

      .sc-icon {
        opacity: 0.8;
      }
    }
  }
}
</style>
